import React, { useCallback, useEffect, useState } from 'react';
import { Bike, CalendarDays, Coins, RefreshCw, History } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { money } from '../lib/constants';
import { PageHeader, Card, Stat, Table, Tr, Td, Avatar, SimpleBadge } from './shared';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

const today = () => new Date().toISOString().slice(0, 10);
const fmtDay = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('es-HN', { weekday: 'short', day: '2-digit', month: 'short' }) : '-');

const DriverHistorySheet = ({ driver, onClose }) => {
  const { driverHistory, orders } = useApp();
  const [range, setRange] = useState({ from: today(), to: today() });
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!driver) return;
    driverHistory({ driverId: driver.driverId, from: range.from, to: range.to }).then(setData).catch(() => setData(null));
  }, [driver, driverHistory, range.from, range.to]);
  useEffect(() => { load(); }, [load, orders]);
  return (
    <Sheet open={!!driver} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="driver-history-sheet">
        <SheetHeader><SheetTitle className="text-xl font-extrabold">Historial de {driver?.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold text-gray-500">Desde
              <input data-testid="history-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} className="block mt-1 h-9 px-3 rounded-lg border border-gray-200 text-sm" />
            </label>
            <label className="text-xs font-semibold text-gray-500">Hasta
              <input data-testid="history-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} className="block mt-1 h-9 px-3 rounded-lg border border-gray-200 text-sm" />
            </label>
            <Button variant="outline" className="h-9 gap-2" onClick={load} data-testid="history-refresh"><RefreshCw size={15} /> Actualizar</Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-emerald-50 p-3"><p className="text-xs text-gray-600">Realizados</p><p data-testid="history-completed" className="text-2xl font-extrabold">{data?.completed ?? '…'}</p></div>
            <div className="rounded-xl bg-red-50 p-3"><p className="text-xs text-gray-600">Rechazados</p><p data-testid="history-cancelled" className="text-2xl font-extrabold">{data?.rejected ?? '…'}</p></div>
            <div className="rounded-xl bg-blue-50 p-3"><p className="text-xs text-gray-600">A pagar</p><p data-testid="history-pay" className="text-2xl font-extrabold">{data ? money(data.pay) : '…'}</p></div>
          </div>
          <Table columns={['# Pedido', 'Fecha', 'Negocio / Origen', 'Cliente', 'Estado', 'Pago']} rows={data?.orders || []} emptyText="Sin pedidos en este rango"
            renderRow={(o) => (
              <Tr key={o.id} data-testid={`history-order-${o.id}`}>
                <Td className="font-semibold">#{o.id}</Td>
                <Td className="whitespace-nowrap text-gray-600">{fmtDay(o.day)} {o.time}</Td>
                <Td>{o.restaurant}</Td>
                <Td className="text-gray-600">{o.customer}</Td>
                <Td><SimpleBadge tone={o.status === 'completed' ? 'green' : 'red'}>{o.status === 'completed' ? 'Realizado' : 'Rechazado'}</SimpleBadge></Td>
                <Td className="font-semibold">{o.status === 'completed' ? money(o.pay) : '-'}</Td>
              </Tr>
            )} />
        </div>
      </SheetContent>
    </Sheet>
  );
};

const AdminDriverStats = () => {
  const { driverStats, orders } = useApp();
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => { driverStats(date).then(setData).catch(() => setData(null)); }, [driverStats, date]);
  useEffect(() => { load(); }, [load, orders]);
  const rows = data?.drivers || [];
  return (
    <div>
      <PageHeader title="Estadísticas de conductores" subtitle="Pedidos completados por día y por semana (lunes a domingo) para calcular el pago de cada conductor"
        actions={<>
          <input data-testid="stats-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-sm" />
          <Button variant="outline" className="h-10 gap-2" onClick={load} data-testid="stats-refresh"><RefreshCw size={16} /> Actualizar</Button>
        </>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Stat label={`Pedidos del ${fmtDay(data?.date || date)}`} value={data?.totals?.dayOrders ?? '…'} icon={Bike} bg="bg-white border border-gray-100" iconBg="bg-[#FDECEC] text-[#EE3B36]" />
        <Stat label="A pagar en el día" value={data ? money(data.totals.dayPay) : '…'} icon={Coins} bg="bg-white border border-gray-100" iconBg="bg-[#E9F7EF] text-[#22A45D]" />
        <Stat label="Pedidos de la semana" value={data?.totals?.weekOrders ?? '…'} icon={CalendarDays} bg="bg-white border border-gray-100" iconBg="bg-[#E8F0FE] text-[#2F80ED]"
          sub={data && <p className="text-xs text-gray-500 mt-1">{fmtDay(data.weekStart)} — {fmtDay(data.weekEnd)}</p>} />
        <Stat label="A pagar en la semana" value={data ? money(data.totals.weekPay) : '…'} icon={Coins} bg="bg-white border border-gray-100" iconBg="bg-[#F3E8FF] text-[#7C3AED]" />
      </div>
      <Card title="Pedidos completados por conductor">
        <div className="p-3">
          <Table columns={['Conductor', 'Pedidos del día', 'A pagar (día)', 'Rechazados (día)', 'Pedidos de la semana', 'A pagar (semana)', 'Rechazados (semana)', 'Estado', 'Historial']} rows={rows} emptyText="Sin conductores registrados"
            renderRow={(d) => (
              <Tr key={d.driverId} data-testid={`driver-stats-row-${d.driverId}`}>
                <Td><span className="flex items-center gap-2.5"><Avatar src={d.avatar} name={d.name} size={34} /><span><span className="font-semibold block">{d.name}</span><span className="text-xs text-gray-500">{d.phone}</span></span></span></Td>
                <Td data-testid={`day-orders-${d.driverId}`} className="font-extrabold text-base">{d.dayOrders}</Td>
                <Td data-testid={`day-pay-${d.driverId}`} className="font-semibold">{money(d.dayPay)}</Td>
                <Td data-testid={`day-rejected-${d.driverId}`} className="text-red-600 font-semibold">{d.dayRejected || 0}</Td>
                <Td data-testid={`week-orders-${d.driverId}`} className="font-extrabold text-base">{d.weekOrders}</Td>
                <Td data-testid={`week-pay-${d.driverId}`} className="font-semibold">{money(d.weekPay)}</Td>
                <Td data-testid={`week-rejected-${d.driverId}`} className="text-red-600 font-semibold">{d.weekRejected || 0}</Td>
                <Td><SimpleBadge tone={d.status === 'active' ? 'green' : 'gray'}>{d.status === 'active' ? 'Activo' : 'Inactivo'}</SimpleBadge></Td>
                <Td><button data-testid={`view-history-${d.driverId}`} onClick={() => setSel(d)} className="h-8 px-3 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-50"><History size={14} /> Ver</button></Td>
              </Tr>
            )} />
        </div>
      </Card>
      <DriverHistorySheet driver={sel} onClose={() => setSel(null)} />
    </div>
  );
};

export default AdminDriverStats;
